import Components from "slotworks/components/components";
import Layers from "slotworks/graphics/layers/layers";
import Bet from "slotworks/model/bets/bet-model";
import GameState from "slotworks/model/game-state";
import SpinRequestPayload from "slotworks/model/gameplay/requests/spin-request-payload";
import CommsManager from "slotworks/server/comms-manager";
import SmartButton from "slotworks/smart-button/smart-button";
import { SmartButtonFlag } from "slotworks/smart-button/smart-button";
import AbstractBonus from "slotworks/state-machine/standard/states/abstract-bonus";
import Contract from "slotworks/utils/contract";
import { sequence } from "slotworks/utils/race-utils";
import BonusRecord from "../integration/revolver/schema/bonus-record";
import BonusWheelComponent from "components/bonus/bonus-wheel";
import ICFreespinRecord from "integration/revolver/schema/freespin-record";
import ICSpinRecord from "integration/revolver/schema/spin-record";
import DSComponents from "components/components";
import { PotType } from "components/pots/coinpots-component";
import soundManager from "slotworks/sound/sound-manager";
import betModel from "slotworks/model/bets/bet-model";
import gameState from "slotworks/model/game-state";
import Logger = require("js-logger");
import deviceInfo from "slotworks/utils/device-info";
import regulation from "slotworks/integration/revolver/regulations/regulation";
import campaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import ShowPromoFSafterFreespins from "slotworks/commands/promo-free-spins/show-pfs-after-freespins";
import wrapperMediator from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import RevolverSettings from "model/revolver-settings";
import { StartBaseMusic, StartBaseMusicMid, StartBaseMusicSoft } from "commands/music";

export default class ICBonus extends AbstractBonus {

    // component wheel
    private bonusWheel: BonusWheelComponent;
    private inFreespins: boolean;

    public onEnter() {
        DSComponents.coinPots.burstCoins(PotType.BONUS).then(() => {
            super.onEnter();

            const record = GameState.getCurrentGame().latestRecord();
            const bonusRecord = record as BonusRecord;
            let multiplier = bonusRecord.multiplier;

            if (record instanceof ICSpinRecord || (record instanceof BonusRecord && !bonusRecord.inFreespins && !bonusRecord.recoversFreespins)) {
                // soundManager.customEvent("base_music_soft");
                RevolverSettings.winCount++;
                RevolverSettings.lastWinTime = Date.now();
                StartBaseMusicMid();

                if (!multiplier) multiplier = 1;
                this.inFreespins = false;
            } else {
                soundManager.customEvent("freespin_music_softer");
                if (!multiplier) multiplier = (record as ICFreespinRecord).addedCoins[2];
                this.inFreespins = true;
            }

            if (Layers.Header.hasScene("freespins")) {
                Layers.Header.setScene("freespins").execute();
            }

            this.showPrompt(multiplier).then(() => {
                this.showWheel();
                this.enterBonus(multiplier);
            });
        });
    }

    private showWheel() {
        this.bonusWheel = new BonusWheelComponent();
        this.bonusWheel.onTriggered.add(() => this.getSpinData());
        this.bonusWheel.onComplete.add((totalWin: number) => {
            this.exitBonus(totalWin);
        });
    }

    private showPrompt(multiplier: number) {
        return new Contract<void>(resolve => {
            const scene = "bonuswheel" + multiplier;
            if (multiplier > 1 && Layers.Prompts.hasScene(scene)) {
                Layers.CelebrationBackground.setScene("bonuswheel").execute();
                Components.prompt.show([scene], new Map<string, string>(), 3000)
                    .then(() => {
                        Layers.CelebrationBackground.defaultScene().execute();
                        resolve(null)
                    });
            } else {
                resolve(null)
            }
        });
    }

    private enterBonus(multiplier: number) {
        soundManager.customEvent("bonus_intro");
        Layers.PromoBetBar.hide();
        Layers.BetBar.hide();
        Layers.SmartButton.hide();
        SmartButton.setFlag(SmartButtonFlag.PROMPT, false);
        Layers.CelebrationBackground.setScene("bigWin")
            .then(() => this.bonusWheel.enterBonus(multiplier));
    }

    private getSpinData() {
        const payload = new SpinRequestPayload();

        CommsManager.request(payload).then(() => {
            let latest = GameState.getCurrentGame().latestRecord();
            let spinData;
            if (latest instanceof BonusRecord) {
                spinData = latest;
            } else {
                spinData = GameState.getCurrentGame().getPreviousRecord() as BonusRecord;
            }
            this.bonusWheel.feedResult(spinData);
        });
    }

    private exitBonus(totalWin: number) {
        sequence([
            () => this.bonusWheel.exitBonus(),
            () => this.showTotalWin()
        ]).then(() => {
            this.hideBonusWheelElements().execute();
            Layers.Header.defaultScene().execute();

            if (!this.inFreespins && campaignsModel.isFreespinsActive()) {
                ShowPromoFSafterFreespins();
            } else {
                Layers.BetBar.show();
                Layers.SmartButton.show();
            }
            SmartButton.setFlag(SmartButtonFlag.BONUS, false);

            const record = GameState.getCurrentGame().latestRecord();
            if (record instanceof BonusRecord) {
                const bonusRecord = record as BonusRecord;
                if (!bonusRecord.inFreespins && !bonusRecord.freespinsTriggered)
                    DSComponents.coinPots.restoreBankedCoinsAfterFreespin();
            }
            wrapperMediator.roundEnd(record.creditsWon, 0, GameState.getCurrentGame().balance);
            regulation.updateNetPosition(GameState.getNetPosition());
            this.complete()

        });
    }

    private shouldShowTotalWin(record: BonusRecord) {
        const latest = gameState.getCurrentGame().latestRecord();
        const isBigWin = Components.bigWin.isBigWin(record.creditsWon, Bet.getTotalBet());
        const isFreespins = ((latest instanceof ICFreespinRecord) && !(latest as ICFreespinRecord).freespinsTriggered) || (latest as BonusRecord).inFreespins;
        const isJackpot = record.isJackpotWon;
        return !isJackpot && (isFreespins || !isBigWin);
    }

    private hideBonusWheelElements(): Contract<void> {
        return Layers.CelebrationBackground.defaultScene();
    }

    private showTotalWin() {
        const record = GameState.getCurrentGame().latestRecord();
        let bonusRecord: BonusRecord;
        if (record instanceof BonusRecord) {
            bonusRecord = record as BonusRecord;
        } else {
            bonusRecord = GameState.getCurrentGame().getPreviousRecord() as BonusRecord;
        }
        if (this.shouldShowTotalWin(bonusRecord)) {
            return new Contract<void>(resolve => {
                DSComponents.celebrateWinMultiplier.totalWin(bonusRecord.awardedMultiplier * bonusRecord.multiplier * betModel.getTotalBet()).then(() => {
                    if (record instanceof ICFreespinRecord || (record instanceof BonusRecord && record.inFreespins)) {
                        soundManager.customEvent("start_freespin_soft");
                        this.resetRecord(true);
                    } else {                        
                        // soundManager.customEvent("base_reels_background_start");
                        StartBaseMusic(true);
                        this.resetRecord(false);
                    }
                    resolve(null);
                });
            });
        }
        return Contract.empty();
    }

    private resetRecord(freespins: boolean) {
        const record = GameState.getCurrentGame().getCurrentRecord() as BonusRecord;

        const triggeringWin = record.creditsWon;
        const totalWin = freespins ? GameState.getCurrentGame().getRunningFreespinsWinTotal() : GameState.getCurrentGame().totalCreditsWon;

        Components.betBar.winnings.setWinnings(triggeringWin, totalWin, 750);
    }
}